'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

interface UnpaidCustomer {
  id: string;
  name: string;
  email: string;
  phone: string;
  service: string;
  invoiceId: string;
  dueDate: string;
  amountDue: number;
  daysPastDue: number;
  type: 'Premium' | 'Corporate' | 'Standard';
}

const unpaidCustomers: UnpaidCustomer[] = [
  {
    id: 'inv-001',
    name: 'Yuki Tanaka',
    email: 'yuki.t@outlook.com',
    phone: '+1 (332) 555-0401',
    service: 'Maid Service – Weekly',
    invoiceId: 'INV-2026-0041',
    dueDate: 'Apr 10, 2026',
    amountDue: 640,
    daysPastDue: 12,
    type: 'Standard',
  },
  {
    id: 'inv-002',
    name: 'Ananya Krishnan',
    email: 'ananya.k@techco.in',
    phone: '+1 (408) 555-0339',
    service: 'Apartment Rental – Monthly',
    invoiceId: 'INV-2026-0038',
    dueDate: 'Apr 5, 2026',
    amountDue: 2800,
    daysPastDue: 17,
    type: 'Standard',
  },
  {
    id: 'inv-003',
    name: 'Carlos Mendez',
    email: 'c.mendez@hotmail.com',
    phone: '+1 (305) 555-0167',
    service: 'Hospitality Package – 3 Nights',
    invoiceId: 'INV-2026-0035',
    dueDate: 'Mar 28, 2026',
    amountDue: 1450,
    daysPastDue: 25,
    type: 'Standard',
  },
  {
    id: 'inv-004',
    name: 'Preethi Nair',
    email: 'preethi.n@nair.in',
    phone: '+91 98765 43210',
    service: 'Maid Service – One-time',
    invoiceId: 'INV-2026-0029',
    dueDate: 'Mar 20, 2026',
    amountDue: 190,
    daysPastDue: 33,
    type: 'Standard',
  },
  {
    id: 'inv-005',
    name: 'Marcus Webb',
    email: 'mwebb@webdev.io',
    phone: '+1 (718) 555-0274',
    service: 'Corporate Suite – Monthly',
    invoiceId: 'INV-2026-0022',
    dueDate: 'Mar 15, 2026',
    amountDue: 5200,
    daysPastDue: 38,
    type: 'Corporate',
  },
  {
    id: 'inv-006',
    name: 'Lena Hoffmann',
    email: 'l.hoffmann@dmail.de',
    phone: '+49 171 555 0283',
    service: 'Hospitality Package – 5 Nights',
    invoiceId: 'INV-2026-0018',
    dueDate: 'Mar 8, 2026',
    amountDue: 3900,
    daysPastDue: 45,
    type: 'Corporate',
  },
  {
    id: 'inv-007',
    name: 'David Okonkwo',
    email: 'd.okonkwo@corp.com',
    phone: '+1 (646) 555-0318',
    service: 'Apartment Rental – Deposit',
    invoiceId: 'INV-2026-0011',
    dueDate: 'Feb 28, 2026',
    amountDue: 4500,
    daysPastDue: 53,
    type: 'Corporate',
  },
];

const urgencyConfig = (days: number) => {
  if (days <= 14) return { label: 'Overdue', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' };
  if (days <= 30) return { label: 'Past Due', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' };
  return { label: 'Critical', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' };
};

const typeColors: Record<string, string> = {
  Premium: 'bg-accent/15 text-amber-700',
  Corporate: 'bg-blue-50 text-blue-700',
  Standard: 'bg-muted text-muted-foreground',
};

export default function UnpaidCustomers() {
  const [search, setSearch] = useState('');
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const totalOutstanding = unpaidCustomers
    .filter((c) => !dismissed.has(c.id))
    .reduce((sum, c) => sum + c.amountDue, 0);

  const filtered = unpaidCustomers
    .filter((c) => !dismissed.has(c.id))
    .filter((c) => {
      const q = search.toLowerCase();
      return !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.invoiceId.toLowerCase().includes(q);
    })
    .sort((a, b) => b.daysPastDue - a.daysPastDue);

  const handleSendReminder = (cust: UnpaidCustomer) => {
    toast.success(`Payment reminder sent to ${cust.name}`);
  };

  const handleMarkPaid = (cust: UnpaidCustomer) => {
    setDismissed((prev) => new Set([...prev, cust.id]));
    toast.success(`${cust.name}'s invoice marked as paid.`);
  };

  return (
    <div className="bg-white rounded-xl border border-border shadow-card overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
            <Icon name="ExclamationCircleIcon" size={18} className="text-red-600" />
          </div>
          <div>
            <h2 className="font-display font-semibold text-base text-foreground flex items-center gap-2">
              Unpaid Customers
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                {filtered.length}
              </span>
            </h2>
            <p className="text-xs text-muted-foreground">
              Outstanding balance:{' '}
              <span className="font-semibold text-red-600 tabular-nums">${totalOutstanding.toLocaleString()}</span>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-muted">
            <Icon name="MagnifyingGlassIcon" size={15} className="text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or invoice..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent text-sm outline-none w-44 text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <button
            onClick={() => toast.success('Bulk reminders sent to all unpaid customers.')}
            className="flex items-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Icon name="BellAlertIcon" size={15} />
            Send All Reminders
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 divide-x divide-border border-b border-border bg-red-50/40">
        {[
          { label: 'Overdue (≤14 days)', count: filtered.filter((c) => c.daysPastDue <= 14).length, color: 'text-amber-600' },
          { label: 'Past Due (15–30 days)', count: filtered.filter((c) => c.daysPastDue > 14 && c.daysPastDue <= 30).length, color: 'text-orange-600' },
          { label: 'Critical (30+ days)', count: filtered.filter((c) => c.daysPastDue > 30).length, color: 'text-red-600' },
        ].map((stat) => (
          <div key={stat.label} className="px-6 py-3 text-center">
            <p className={`text-xl font-bold tabular-nums ${stat.color}`}>{stat.count}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {['Customer', 'Type', 'Service', 'Invoice ID', 'Due Date', 'Days Overdue', 'Amount Due', 'Status', 'Actions'].map((col) => (
                <th
                  key={col}
                  className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center">
                      <Icon name="CheckCircleIcon" size={24} className="text-emerald-500" />
                    </div>
                    <p className="font-display font-semibold text-foreground">All payments settled</p>
                    <p className="text-sm text-muted-foreground">No outstanding invoices at this time</p>
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((cust, idx) => {
                const urgency = urgencyConfig(cust.daysPastDue);
                return (
                  <tr
                    key={cust.id}
                    className={`border-b border-border/50 hover:bg-muted/30 transition-colors group ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}
                  >
                    {/* Customer */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-red-700 text-xs font-bold">
                            {cust.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground whitespace-nowrap">{cust.name}</p>
                          <p className="text-xs text-muted-foreground">{cust.email}</p>
                        </div>
                      </div>
                    </td>
                    {/* Type */}
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeColors[cust.type]}`}>
                        {cust.type}
                      </span>
                    </td>
                    {/* Service */}
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{cust.service}</td>
                    {/* Invoice */}
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-foreground bg-muted px-2 py-0.5 rounded">{cust.invoiceId}</span>
                    </td>
                    {/* Due Date */}
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{cust.dueDate}</td>
                    {/* Days Overdue */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full ${urgency.bg} ${urgency.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${urgency.dot}`} />
                        {cust.daysPastDue}d
                      </span>
                    </td>
                    {/* Amount Due */}
                    <td className="px-4 py-3">
                      <span className="text-sm font-bold text-red-600 tabular-nums">${cust.amountDue.toLocaleString()}</span>
                    </td>
                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${urgency.bg} ${urgency.text}`}>
                        {urgency.label}
                      </span>
                    </td>
                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          title="Send payment reminder"
                          onClick={() => handleSendReminder(cust)}
                          className="p-1.5 rounded-lg hover:bg-amber-50 text-muted-foreground hover:text-amber-600 transition-colors"
                        >
                          <Icon name="BellAlertIcon" size={15} />
                        </button>
                        <button
                          title="Mark as paid"
                          onClick={() => handleMarkPaid(cust)}
                          className="p-1.5 rounded-lg hover:bg-emerald-50 text-muted-foreground hover:text-emerald-600 transition-colors"
                        >
                          <Icon name="CheckCircleIcon" size={15} />
                        </button>
                        <button
                          title="View invoice"
                          onClick={() => toast.success(`Opening invoice ${cust.invoiceId}`)}
                          className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                        >
                          <Icon name="DocumentTextIcon" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-border bg-muted/20 flex flex-wrap items-center gap-6 text-sm">
        <span className="text-muted-foreground">
          Unpaid invoices: <span className="font-semibold text-foreground">{filtered.length}</span>
        </span>
        <span className="text-muted-foreground">
          Total outstanding:{' '}
          <span className="font-bold text-red-600 tabular-nums">${totalOutstanding.toLocaleString()}</span>
        </span>
        <span className="text-muted-foreground">
          Avg. overdue:{' '}
          <span className="font-semibold text-foreground tabular-nums">
            {filtered.length > 0 ? Math.round(filtered.reduce((s, c) => s + c.daysPastDue, 0) / filtered.length) : 0} days
          </span>
        </span>
      </div>
    </div>
  );
}
